// src/pages/About/OurStrategies.jsx
import React, { useState } from 'react';
import Header from '../../components/common/Header/Header';
import Footer from '../../components/common/Footer/Footer';
import Layout from '../../components/common/Layout/Layout';
import { ChevronDown } from 'lucide-react';
import QuerySection from '../../components/common/QuerySection';

const OurStrategies = () => {
    const [activeDropdown, setActiveDropdown] = useState(null);

    const faqItems = [
        { id: 1, question: "What are the tax benefits of mutual fund investments?" },
        { id: 2, question: "How does systematic investment planning (SIP) work?" },
        { id: 3, question: "What risks are covered under portfolio insurance?" },
        { id: 4, question: "What are the benefits of long-term wealth creation?" }
    ];

    const toggleDropdown = (id) => {
        setActiveDropdown(activeDropdown === id ? null : id);
    };

    return (
        <>
            <style>{`
                * {
                    box-sizing: border-box;
                    margin: 0;
                    padding: 0;
                }
                
                /* Performance optimizations */
                .strategy-card, .hero-container {
                    will-change: transform;
                    transform: translateZ(0);
                }
                
                /* Smooth transitions */
                .strategy-card {
                    transition: transform 0.2s ease;
                }

                .strategy-card:hover {
                    transform: translateY(-3px);
                }
                
                /* Mobile-first responsive design */
                .hero-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    gap: 2rem;
                    padding: 1rem;
                }
                
                .strategies-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 1.5rem;
                }
                
                /* Tablet styles */
                @media (min-width: 768px) {
                    .hero-container {
                        padding: 2rem;
                        gap: 3rem;
                    }
                    
                    .strategies-grid {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 2rem;
                    }
                }
                
                /* Desktop styles */
                @media (min-width: 1024px) {
                    .hero-container {
                        flex-direction: row;
                        text-align: left;
                        gap: 4rem;
                        min-height: 400px;
                    }
                    
                    .strategies-grid {
                        grid-template-columns: repeat(4, 1fr);
                    }
                }
                
                /* Typography scaling */
                .hero-title {
                    font-size: clamp(2rem, 5vw, 4rem);
                    line-height: 1.2;
                }
                
                .section-title {
                    font-size: clamp(1.5rem, 4vw, 2.5rem);
                }
                
                .card-title {
                    font-size: clamp(1rem, 2.5vw, 1.3rem);
                }
                
                /* Image optimization */
                img {
                    max-width: 100%;
                    height: auto;
                }
                
                /* Safe area for iOS devices */
                @supports (padding: max(0px)) {
                    .hero-container, .strategies-container {
                        padding-left: max(1rem, env(safe-area-inset-left));
                        padding-right: max(1rem, env(safe-area-inset-right));
                    }
                }
            `}</style>

            <div style={{ minHeight: '100vh', backgroundColor: '#ffffff' }}>
                {/* Header */}
                <Header />

                {/* Hero Section */}
                <section style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    padding: 'clamp(3rem, 8vw, 6rem) 0',
                    position: 'relative',
                    minHeight: 'clamp(400px, 60vh, 500px)',
                    border: '2px solid rgba(255, 255, 255, 0.1)'
                }}>
                    {/* Glass overlay with gradient */}
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.5) 0%, rgba(59, 130, 246, 0.4) 50%, rgba(0, 0, 0, 0.6) 100%)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                    }} />
                    
                    <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        position: 'relative',
                        zIndex: 1
                    }} className="hero-container">
                        <div style={{ 
                            textAlign: 'center',
                            maxWidth: '800px',
                            margin: '0 auto'
                        }}>
                            <h1 style={{
                                fontWeight: '700',
                                color: '#ffffff',
                                margin: '0 0 1rem 0',
                                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                            }} className="hero-title">
                                Our Investment Strategies
                            </h1>
                            <p style={{
                                fontSize: 'clamp(1rem, 2.5vw, 1.3rem)',
                                color: '#f3f4f6',
                                margin: '0',
                                lineHeight: '1.6',
                                maxWidth: '600px',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                            }}>
                                Comprehensive wealth management solutions tailored for every life stage and financial objective*
                            </p>
                        </div>
                    </div>
                </section>

                {/* Core Investment Strategies */}
                <section style={{
                    backgroundColor: '#f8fafc',
                    padding: 'clamp(3rem, 8vw, 6rem) 0'
                }} className="strategies-section">
                    <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        padding: '0 1rem'
                    }} className="strategies-container">
                        <div style={{
                            textAlign: 'center',
                            marginBottom: 'clamp(2rem, 6vw, 4rem)'
                        }}>
                            <h2 style={{
                                fontWeight: '700',
                                color: '#1a1a1a',
                                marginBottom: '1rem'
                            }} className="section-title">
                                Foundation Investment Strategies
                            </h2>
                            <p style={{
                                fontSize: 'clamp(1rem, 2vw, 1.1rem)',
                                color: '#6b7280',
                                maxWidth: '600px',
                                margin: '0 auto'
                            }}>
                                Core investment approaches designed to address fundamental financial planning needs*
                            </p>
                        </div>

                        <div className="strategies-grid">
                            {/* Retirement Planning Strategy */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1633158829875-e5316a358c6f?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '280px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="strategy-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(245, 158, 11, 0.3) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Retirement Planning Strategy
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        Structured retirement planning that helps determine investment requirements and creates systematic withdrawal frameworks for post-retirement financial security.*
                                    </p>
                                </div>
                            </div>

                            {/* Wealth Generation Strategy */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1559081311-2bea96436b7d?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '280px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="strategy-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(16, 185, 129, 0.3) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Wealth Generation Strategy
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        Long-term wealth creation approach that utilizes disciplined investment strategies to build substantial wealth through systematic portfolio management over time.*
                                    </p>
                                </div>
                            </div>

                            {/* Financial Independence Strategy */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1579952363873-27d3bfad9c0d?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '280px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="strategy-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(239, 68, 68, 0.3) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Financial Independence Strategy
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        Strategic approach to achieve financial independence through equity investments and systematic wealth building for early retirement considerations.*
                                    </p>
                                </div>
                            </div>

                            {/* Tax Optimization Strategy */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '280px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="strategy-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(147, 51, 234, 0.3) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Tax Optimization Strategy
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        Tax-efficient investment planning that utilizes available provisions to minimize tax impact while building wealth through strategic fund selection.*
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Advanced Portfolio Strategies */}
                <section style={{
                    backgroundColor: '#ffffff',
                    padding: 'clamp(3rem, 8vw, 6rem) 0'
                }}>
                    <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        padding: '0 1rem'
                    }}>
                        <div style={{
                            textAlign: 'center',
                            marginBottom: 'clamp(2rem, 6vw, 4rem)'
                        }}>
                            <h2 style={{
                                fontWeight: '700',
                                color: '#1a1a1a',
                                marginBottom: '1rem'
                            }} className="section-title">
                                Advanced Portfolio Management
                            </h2>
                            <p style={{
                                fontSize: 'clamp(1rem, 2vw, 1.1rem)',
                                color: '#6b7280',
                                maxWidth: '600px',
                                margin: '0 auto'
                            }}>
                                Sophisticated investment strategies for comprehensive wealth management and succession planning*
                            </p>
                        </div>

                        <div className="strategies-grid">
                            {/* Intergenerational Wealth Strategy */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1588681664899-f142ff2dc9b1?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '280px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="strategy-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(6, 182, 212, 0.3) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Intergenerational Value Strategy
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        Multi-generational wealth planning that ensures smooth wealth transition through systematic portfolio design and inheritance planning strategies.*
                                    </p>
                                </div>
                            </div>

                            {/* Family Wealth Management */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1584467735867-4442972dc5ee?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '280px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="strategy-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(245, 158, 11, 0.3) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Family Wealth Management
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        Comprehensive family wealth management that structures investment portfolios to optimize for tax efficiency and long-term wealth preservation.*
                                    </p>
                                </div>
                            </div>

                            {/* Income Distribution Strategy */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1544377193-33dcf4d68fb5?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '280px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="strategy-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(16, 185, 129, 0.3) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Income Distribution Strategy
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        Strategic income distribution planning that creates structured approaches to manage family income efficiently while considering tax implications.*
                                    </p>
                                </div>
                            </div>

                            {/* Estate Planning Strategy */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1423784346385-c1d4dac9893a?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '280px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="strategy-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(147, 51, 234, 0.3) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Estate Planning Strategy
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        Estate planning facilitation that provides guidance for wealth stewardship and succession planning considerations for future generations.*
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Tactical Investment Approaches */}
                <section style={{
                    backgroundColor: '#f8fafc',
                    padding: 'clamp(3rem, 8vw, 6rem) 0'
                }}>
                    <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        padding: '0 1rem'
                    }}>
                        <div style={{
                            textAlign: 'center',
                            marginBottom: 'clamp(2rem, 6vw, 4rem)'
                        }}>
                            <h2 style={{
                                fontWeight: '700',
                                color: '#1a1a1a',
                                marginBottom: '1rem'
                            }} className="section-title">
                                Tactical Investment Approaches
                            </h2>
                            <p style={{
                                fontSize: 'clamp(1rem, 2vw, 1.1rem)',
                                color: '#6b7280',
                                maxWidth: '600px',
                                margin: '0 auto'
                            }}>
                                Dynamic investment strategies that incorporate behavioral finance principles and market timing considerations*
                            </p>
                        </div>

                        <div className="strategies-grid">
                            {/* Market Opportunity Strategy */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '280px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="strategy-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(16, 185, 129, 0.3) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Market Opportunity Strategy
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        Strategic approach to capitalize on favorable market conditions while funding life aspirations through systematic profit realization methods.*
                                    </p>
                                </div>
                            </div>

                            {/* Risk Management Strategy */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1518186285589-2f7649de83e0?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '280px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="strategy-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(239, 68, 68, 0.3) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Risk Management Strategy
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        Volatility management approach that utilizes diversified fund structures to manage portfolio fluctuations and behavioral investment challenges.*
                                    </p>
                                </div>
                            </div>

                            {/* Systematic Investment Strategy */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1590479773265-7464e5d48118?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '280px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="strategy-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(6, 182, 212, 0.3) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Systematic Investment Strategy
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        Disciplined investment approach that works consistently through SIPs to gradually build wealth over extended time periods with rupee cost averaging benefits.*
                                    </p>
                                </div>
                            </div>

                            {/* Goal-Based Rebalancing */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1565514020179-026b92b84bb6?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '280px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="strategy-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(245, 158, 11, 0.3) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Goal-Based Rebalancing
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        Strategic rebalancing that reallocates investments from equity to safer assets as financial goals approach, managing risk-return profile effectively.*
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Specialized Planning Strategies */}
                <section style={{
                    backgroundColor: '#ffffff',
                    padding: 'clamp(3rem, 8vw, 6rem) 0'
                }}>
                    <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        padding: '0 1rem'
                    }}>
                        <div style={{
                            textAlign: 'center',
                            marginBottom: 'clamp(2rem, 6vw, 4rem)'
                        }}>
                            <h2 style={{
                                fontWeight: '700',
                                color: '#1a1a1a',
                                marginBottom: '1rem'
                            }} className="section-title">
                                Specialized Planning Solutions
                            </h2>
                            <p style={{
                                fontSize: 'clamp(1rem, 2vw, 1.1rem)',
                                color: '#6b7280',
                                maxWidth: '600px',
                                margin: '0 auto'
                            }}>
                                Customized planning frameworks designed for specific life events and financial objectives*
                            </p>
                        </div>

                        <div className="strategies-grid">
                            {/* Present Value Planning */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1416879595882-3373a0480b5b?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '280px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="strategy-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(59, 130, 246, 0.3) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Present Value Planning
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        Mathematical planning approach that utilizes present value calculations to align current investments with future life goals and financial requirements.*
                                    </p>
                                </div>
                            </div>

                            {/* Cash Flow Planning */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1611224923853-80b023f02d71?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '280px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="strategy-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(245, 158, 11, 0.3) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Cash Flow Planning
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        Strategic framework for major financial decisions that anchors investment choices in long-term cash flow visibility and planning considerations.*
                                    </p>
                                </div>
                            </div>

                            {/* Sabbatical Planning */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1436450412740-6b988f486c6b?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '280px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="strategy-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(16, 185, 129, 0.3) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Sabbatical Planning
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        Time-centric planning approach that helps fund sabbaticals, career transitions, or extended breaks through systematic investment planning.*
                                    </p>
                                </div>
                            </div>

                            {/* Emergency Fund Strategy */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1551524164-687a55dd1126?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '280px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="strategy-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(147, 51, 234, 0.3) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Emergency Fund Strategy
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        Safety net framework that builds buffer portfolios to ensure financial resilience during unexpected events and market downturns.*
                                    </p>
                                </div>
                            </div>

                            {/* Dream Funding Strategy */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '280px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="strategy-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(239, 68, 68, 0.3) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Dream Funding Strategy
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        Pre-funding approach that utilizes current money value to secure future aspirational experiences through dedicated investment allocation strategies.*
                                    </p>
                                </div>
                            </div>

                            {/* Behavioral Investment Strategy */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1604594849809-dfedbc827105?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '280px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="strategy-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(6, 182, 212, 0.3) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Behavioral Investment Strategy
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        Engagement approach that transforms SIPs into emotional goal engines by connecting systematic investment flows to specific future aspirations.*
                                    </p>
                                </div>
                            </div>

                            {/* Lifestyle Protection Strategy */}
                            <div style={{
                                backgroundImage: 'url(https://images.unsplash.com/photo-1565728744382-61accd4aa148?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                borderRadius: '12px',
                                padding: '2rem',
                                position: 'relative',
                                overflow: 'hidden',
                                minHeight: '280px',
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }} className="strategy-card">
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(16, 185, 129, 0.3) 100%)'
                                }} />
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <h3 style={{
                                        fontWeight: '700',
                                        color: '#ffffff',
                                        marginBottom: '1rem',
                                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                                    }} className="card-title">
                                        Lifestyle Protection Strategy
                                    </h3>
                                    <p style={{
                                        color: '#f3f4f6',
                                        lineHeight: '1.6',
                                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                    }}>
                                        Comprehensive protection strategy that safeguards essential lifestyle requirements like healthcare and housing through stable income generation.*
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Important Notice */}
                <section style={{
                    backgroundColor: '#1f2937',
                    padding: '3rem 0',
                    color: '#ffffff'
                }}>
                    <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto',
                        padding: '0 1rem',
                        textAlign: 'center'
                    }}>
                        <p style={{
                            fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                            lineHeight: '1.6',
                            marginBottom: '1rem',
                            color: '#d1d5db'
                        }}>
                            *All strategies mentioned are subject to market risks and regulatory changes. Past performance does not guarantee future results. 
                            Please consult with our mutual fund distributors before making investment decisions.
                        </p>
                        <p style={{
                            fontSize: 'clamp(0.8rem, 1.8vw, 1rem)',
                            color: '#9ca3af'
                        }}>
                            WinNest is an AMFI Registered Mutual Fund Distributor (ARN-81431). Mutual fund investments are subject to market risks, read all scheme related documents carefully.
                        </p>
                    </div>
                </section>

                {/* Footer */}
                <Footer />
            </div>
        </>
    );
};
export default OurStrategies;